/**
 * API Client with Azure AD (MSAL) Support
 *
 * Makes authenticated requests using Authorization: Bearer <token>.
 * Automatically handles token refresh on 401 responses.
 */

import { authService, getAccessToken, getCurrentUserId } from '@/services/auth-service'
import { logError, logWarn, logInfo } from '@/lib/logger'
import { env } from '@/lib/env'

const BACKEND_URL = env.backendApiUrl
const API_PREFIX = '/api/v1'
const CONTEXT = 'ApiClient'

/**
 * Text of a FastAPI `detail`: a string as is, cdb_tqs's `{code, message}`
 * (passed through by the BFF) as its message.
 */
export function errorDetailText(detail: unknown): string | null {
  if (typeof detail === 'string') return detail || null
  if (detail && typeof detail === 'object' && typeof (detail as { message?: unknown }).message === 'string') {
    return (detail as { message: string }).message
  }
  return null
}

/**
 * A failed BFF request. `message` is what callers always got; `code` is the
 * BFF's stable `detail.code` where it sends one (`{code, message}`), so the UI
 * can branch on it instead of on the text.
 */
export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message)
    this.name = 'ApiRequestError'
  }
}

function errorDetailCode(detail: unknown): string | undefined {
  if (detail && typeof detail === 'object' && typeof (detail as { code?: unknown }).code === 'string') {
    return (detail as { code: string }).code
  }
  return undefined
}

/**
 * Synchronous JSON request headers: Content-Type plus Bearer token if available.
 * For callers doing raw fetch() against the BFF outside of apiRequest.
 */
export function getAuthHeaders(): HeadersInit {
  const token = getAccessToken()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

/**
 * Get authentication headers with proactive token refresh.
 * If token is expiring soon, refresh it before making the request.
 */
async function resolveAuthHeaders(): Promise<Record<string, string>> {
  let token = getAccessToken()

  // If no token, try to refresh proactively
  if (!token) {
    logInfo({ context: CONTEXT }, 'Token expired or expiring, attempting proactive refresh')
    token = await authService.refreshAccessToken()
  }

  if (token) {
    return { 'Authorization': `Bearer ${token}` }
  }

  // No X-User-Id fallback: a header the client picks freely is not proof of
  // identity, and the BFF used to accept it as one (#522). Without a token the
  // request goes out unauthenticated and fails with 401, which is the honest
  // outcome — the caller has to sign in again.
  logWarn({ context: CONTEXT }, 'No authentication available')
  return {}
}

/**
 * Check if we have valid authentication.
 */
function hasAuthentication(): boolean {
  return getAccessToken() !== null || getCurrentUserId() !== null
}

/**
 * Make an authenticated request to the backend API.
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  retryOnUnauth = true
): Promise<T | null> {
  if (!hasAuthentication()) {
    logError(new Error('No authentication available - not logged in'), { context: CONTEXT })
    throw new Error('Nicht angemeldet')
  }

  const url = `${BACKEND_URL}${API_PREFIX}${endpoint}`

  try {
    // Get auth headers with proactive token refresh
    const authHeaders = await resolveAuthHeaders()

    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))

      if (response.status === 401) {
        // Token might be expired - try to refresh
        if (retryOnUnauth) {
          logWarn({ context: CONTEXT }, '401 received, attempting token refresh')
          const newToken = await authService.refreshAccessToken()

          if (newToken) {
            logInfo({ context: CONTEXT }, 'Token refreshed, retrying request')
            // Retry the request with new token (but don't retry again if it fails)
            return apiRequest<T>(endpoint, options, false)
          }
        }

        // Could not refresh - user should re-login
        logError(new Error('Session expired and could not refresh'), { context: CONTEXT })
        throw new Error('Sitzung abgelaufen. Bitte erneut anmelden.')
      }

      if (response.status === 403) {
        throw new ApiRequestError(
          errorDetailText(errorData.detail) || 'Keine Berechtigung',
          403,
          errorDetailCode(errorData.detail),
        )
      }

      if (response.status === 404) {
        return null
      }

      throw new ApiRequestError(
        errorDetailText(errorData.detail) || `Fehler: ${response.status}`,
        response.status,
        errorDetailCode(errorData.detail),
      )
    }

    if (response.status === 204) {
      return null
    }

    return await response.json()
  } catch (error) {
    logError(error instanceof Error ? error : new Error(String(error)), { context: CONTEXT, endpoint })
    throw error
  }
}

/**
 * GET request
 */
export async function apiGet<T>(endpoint: string): Promise<T | null> {
  return apiRequest<T>(endpoint, { method: 'GET' })
}

/**
 * POST request
 */
export async function apiPost<T>(endpoint: string, body?: unknown): Promise<T | null> {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  })
}

/**
 * PATCH request
 */
export async function apiPatch<T>(endpoint: string, body: unknown): Promise<T | null> {
  return apiRequest<T>(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

/**
 * DELETE request
 */
export async function apiDelete(endpoint: string): Promise<void> {
  await apiRequest(endpoint, { method: 'DELETE' })
}
